import { useEffect, useRef } from 'react';
import SortableTh from '@/client/components/common/SortableTh';
import { Skeleton } from '@/components/ui/skeleton';
import type { AdminUserListRow, UserAccountRoleType } from '@/client/types/admin/users-orgs';
import { UserTableRow } from './UserTableRow';

const COLUMN_COUNT = 11;
const thClass = 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

const LoadingRow: React.FC = () => (
  <tr>
    <td className="w-10 px-4 py-4" />
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="ml-4 h-4 w-32" />
      </div>
    </td>
    {Array.from({ length: COLUMN_COUNT - 2 }).map((_, cell) => (
      <td key={cell} className="px-6 py-4 whitespace-nowrap">
        <Skeleton className="h-4 w-20" />
      </td>
    ))}
  </tr>
);

/** Header checkbox: ticked when the whole page is selected, dashed when part of it is. */
const PageCheckbox: React.FC<{ checked: boolean; indeterminate: boolean; onChange: (v: boolean) => void }> = ({
  checked,
  indeterminate,
  onChange,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="h-4 w-4 cursor-pointer rounded border-gray-300"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      aria-label="Select all users on this page"
    />
  );
};

export const UsersTable: React.FC<{
  rows: AdminUserListRow[];
  loading: boolean;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onToggleSort: (field: string) => void;
  onRowClick: (uid: number) => void;
  roleTypeById: Map<number, UserAccountRoleType>;
  isSelected: (uid: number) => boolean;
  onToggleRow: (uid: number) => void;
  onTogglePage: (checked: boolean) => void;
}> = ({ rows, loading, limit, sortBy, sortOrder, onToggleSort, onRowClick, roleTypeById, isSelected, onToggleRow, onTogglePage }) => {
  const selectedOnPage = rows.filter(({ user }) => isSelected(user.uid)).length;
  const sort = { sortBy, sortOrder, onToggle: onToggleSort };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-10 px-4 py-3">
              <PageCheckbox
                checked={rows.length > 0 && selectedOnPage === rows.length}
                indeterminate={selectedOnPage > 0 && selectedOnPage < rows.length}
                onChange={onTogglePage}
              />
            </th>
            <SortableTh label="Name" field="name" {...sort} />
            <SortableTh label="Email" field="email" {...sort} />
            <th className={thClass}>Phones</th>
            <th className={thClass}>Profile</th>
            <th className={thClass}>Completion</th>
            <th className={thClass}>Verification</th>
            <SortableTh label="Status" field="status" {...sort} />
            <SortableTh label="Role" field="role" {...sort} />
            <SortableTh label="Created" field="created" {...sort} />
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading && Array.from({ length: limit }).map((_, i) => <LoadingRow key={i} />)}

          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={COLUMN_COUNT} className="px-6 py-12 text-center text-sm text-gray-400">
                No users match the current filters.
              </td>
            </tr>
          )}

          {!loading &&
            rows.map(({ user, roles }) => (
              <UserTableRow
                key={user.uid}
                user={user}
                roles={roles}
                roleTypeById={roleTypeById}
                onClick={onRowClick}
                selected={isSelected(user.uid)}
                onToggle={onToggleRow}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
};
